import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "./supabaseClient";
import { useAuth } from "./AuthContext";
import ImageViewer from "./ImageViewer";
import { LevelBadge, MannerBadge } from "./Badges";
import pickleballIcon from "./pickleball.png";
import NotificationBell, { NotificationSettingsModal } from "./NotificationBell";
import useNickname from "./hooks/useNickname";
import TeamMemberPanel from "./components/TeamMemberPanel";
import { openExternalUrl } from "./utils/openExternalUrl";
import { formatSportLevel, formatRecordLevel, formatRecord, formatManner } from "./utils/teamLevel";


const SPORTS = {
  tennis:      { icon: "🎾", label: "테니스" },
  soccer:      { icon: "⚽", label: "축구" },
  futsal:      { icon: "🥅", label: "풋살" },
  badminton:   { icon: "🏸", label: "배드민턴" },
  basketball:  { icon: "🏀", label: "농구" },
  bowling:     { icon: "🎳", label: "볼링" },
  tabletennis: { icon: "🏓", label: "탁구" },
  baseball:    { icon: "⚾", label: "야구" },
  golf:        { icon: "⛳", label: "골프" },
  running:     { icon: null, label: "피클볼" },
  billiards:   { icon: "🎱", label: "당구" },
};

function parseAgeGroup(ag) {
  if (!ag) return "";
  if (Array.isArray(ag)) return ag.join(", ");
  try { const p = JSON.parse(ag); return Array.isArray(p) ? p.join(", ") : ag; } catch { return ag; }
}

function parseArray(val) {
  if (!val) return [];
  if (Array.isArray(val)) return val;
  try { const p = JSON.parse(val); return Array.isArray(p) ? p : []; } catch { return []; }
}

/* ─────── 메뉴 행 컴포넌트 ─────── */
function MenuRow({ label, onClick, color, rightText }) {
  return (
    <div style={s.menuRow} onClick={onClick}>
      <span style={{ ...s.menuLabel, ...(color ? { color } : {}) }}>{label}</span>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {rightText && <span style={{ fontSize: 13, color: "#9ca3af", fontWeight: 500 }}>{rightText}</span>}
        {onClick && <span style={s.menuArrow}>{">"}</span>}
      </div>
    </div>
  );
}

const APP_VERSION = "1.9";
const PLAY_STORE_URL = "https://play.google.com/apps/testing/com.crewpick.android";

export default function TeamManage({ onRegisterNew, onEditTeam, onHome, onTeamSearch, onClubRecruitment, onBoard, onNotice, onInquiry, onPrivacy, onSupport, onViewProfile, onMyMatchEvents, onGuide }) {
  const { user, activeTeam, setActiveTeam, signOut } = useAuth();
  const [teams, setTeams]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showTeamPicker, setShowTeamPicker] = useState(false);

  // 닉네임
  const { nickname, refetch: refetchNick } = useNickname();
  // 약관 모달
  const [termsModal, setTermsModal] = useState(null); // "terms" | "privacy" | null
  // 로그아웃 확인 모달
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  // 계정 삭제
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showNotifSettings, setShowNotifSettings] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [toast, setToast] = useState(null);
  // 팀 삭제
  const [deleteTarget, setDeleteTarget] = useState(null); // 삭제할 팀 객체
  const [teamDeleting, setTeamDeleting] = useState(false);

  const [pullY, setPullY] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const touchStartY = useRef(0);
  const containerRef = useRef(null);

  const handleTouchStart = useCallback((e) => {
    if (containerRef.current && containerRef.current.scrollTop === 0) {
      touchStartY.current = e.touches[0].clientY;
    } else {
      touchStartY.current = 0;
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    if (!touchStartY.current) return;
    const diff = e.touches[0].clientY - touchStartY.current;
    if (diff > 0) setPullY(Math.min(diff * 0.4, 80));
  }, []);

  const handleTouchEnd = useCallback(async () => {
    if (pullY > 50) {
      setRefreshing(true);
      await fetchMyTeams();
      setRefreshing(false);
    }
    setPullY(0);
    touchStartY.current = 0;
  }, [pullY]);

  useEffect(() => {
    if (user) {
      fetchMyTeams();
    }
  }, [user]);

  const fetchMyTeams = async () => {
    setLoading(true);

    // ① 내가 팀장인 팀 (teams.user_id 기준)
    const { data: ownTeams } = await supabase
      .from("teams").select("*").eq("user_id", user.id)
      .order("created_at", { ascending: false });

    // ② 내가 팀원으로 수락된 팀 ID 목록
    const { data: memberships } = await supabase
      .from("team_members").select("team_id")
      .eq("user_id", user.id).eq("status", "accepted");

    const ownIds = new Set((ownTeams || []).map(t => t.id));
    const extraIds = (memberships || []).map(m => m.team_id).filter(id => !ownIds.has(id));

    let memberTeams = [];
    if (extraIds.length > 0) {
      const { data: mt } = await supabase.from("teams").select("*").in("id", extraIds);
      memberTeams = mt || [];
    }

    // ③ 합치기 + myRole 부여
    const list = [
      ...(ownTeams  || []).map(t => ({ ...t, myRole: "leader" })),
      ...memberTeams.map(t => ({ ...t, myRole: "member" })),
    ];
    setTeams(list);
    if (list.length > 0 && !activeTeam) {
      setActiveTeam(list[0]);
    }
    setLoading(false);
  };

  const handleSignOut = () => setShowLogoutConfirm(true);

  const handleVersionCheck = async () => {
    try {
      const { data, error } = await supabase
        .from("app_config")
        .select("value")
        .eq("key", "latest_android_version")
        .single();

      if (error || !data) {
        alert("버전 정보를 불러오지 못했습니다.");
        return;
      }

      const latestVersion = data.value;
      if (latestVersion === APP_VERSION) {
        alert(`현재 최신 버전입니다 (v${APP_VERSION})`);
      } else {
        alert(`새 버전(v${latestVersion})이 있습니다. Play Store에서 업데이트해주세요.`);
        await openExternalUrl(PLAY_STORE_URL);
      }
    } catch (e) {
      alert("버전 정보를 불러오지 못했습니다.");
    }
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2500);
  };

  const handleDeleteAccount = async () => {
    setDeleteLoading(true);
    const { error } = await supabase.rpc("delete_user_account");
    setDeleteLoading(false);
    if (error) {
      setShowDeleteConfirm(false);
      showToast("삭제 중 오류가 발생했습니다");
      return;
    }
    setShowDeleteConfirm(false);
    await signOut();
  };

  // 팀 삭제 실행
  const confirmDeleteTeam = async () => {
    if (!deleteTarget || teamDeleting) return;
    setTeamDeleting(true);
    const wasActive = activeTeam?.id === deleteTarget.id;
    try {
      const { error } = await supabase.from("teams").delete().eq("id", deleteTarget.id);
      if (error) throw error;

      // 남은 팀 목록 재조회 (자동 선택 로직 거치지 않고 직접 반영)
      const { data } = await supabase
        .from("teams")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });
      const list = data || [];
      setTeams(list);

      // 삭제한 팀이 활성팀이면 활성팀 해제 + localStorage 비움 → "팀을 선택해주세요" 상태
      if (wasActive) setActiveTeam(null);
      if (list.length === 0) setShowTeamPicker(false);

      setDeleteTarget(null);
      showToast("팀이 삭제되었습니다");
    } catch (e) {
      // teams가 matches/applications 등과 연결되어 삭제 실패할 수 있음 → 앱 멈춤 방지
      setDeleteTarget(null);
      showToast("진행 중인 경기나 신청이 있어 삭제할 수 없습니다");
    } finally {
      setTeamDeleting(false);
    }
  };

  // MyTeamDetailModal의 팀 삭제 버튼 핸들러 (단계별 검증 포함)
  const handleDeleteTeamFromModal = async (team) => {
    // 1단계 - 팀원 수 확인
    const { count: memCount } = await supabase
      .from("team_members")
      .select("id", { count: "exact", head: true })
      .eq("team_id", team.id)
      .in("status", ["accepted", "active"]);
    if ((memCount || 0) >= 2) {
      alert("팀에 다른 팀원이 있어요. 팀원을 내보내거나 팀장을 양도한 후 삭제할 수 있어요.");
      return;
    }

    // 2단계 - 진행 중인 모집글/신청 확인
    const [{ count: postCount }, { count: appCount }] = await Promise.all([
      supabase.from("posts").select("id", { count: "exact", head: true }).eq("team_id", team.id),
      supabase.from("applications").select("id", { count: "exact", head: true })
        .or(`host_team_id.eq.${team.id},guest_team_id.eq.${team.id}`),
    ]);
    if ((postCount || 0) > 0 || (appCount || 0) > 0) {
      alert("진행 중인 교류전 모집글이나 신청이 있어요. 먼저 정리한 후 삭제해주세요.");
      return;
    }

    // 3단계 - 최종 확인
    if (!window.confirm("팀을 삭제하면 되돌릴 수 없어요. 정말 삭제하시겠어요?")) return;

    // 4단계 - 삭제 실행
    const wasActive = activeTeam?.id === team.id;
    const { error } = await supabase.from("teams").delete().eq("id", team.id);
    if (error) { alert("팀 삭제 중 오류가 발생했어요."); return; }
    alert("팀이 삭제되었어요.");
    setSelectedTeam(null);
    if (wasActive) setActiveTeam(null);
    fetchMyTeams();
  };

  const userInitial = (nickname || user?.user_metadata?.full_name || user?.user_metadata?.name || user?.email || "?")[0]?.toUpperCase() || "?";

  return (
    <div ref={containerRef} style={s.container} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
      {(pullY > 0 || refreshing) && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", height: refreshing ? 40 : pullY, overflow: "hidden", transition: refreshing ? "height 0.2s" : "none", background: "#fff" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ ...s.spinner, width: 20, height: 20, borderWidth: 2 }} />
            <span style={{ fontSize: 12, color: "#9ca3af" }}>{refreshing ? "새로고침 중..." : pullY > 50 ? "놓으면 새로고침" : "당겨서 새로고침"}</span>
          </div>
        </div>
      )}

      {/* 헤더 */}
      <div style={s.header}>
        <div style={s.logoRow}>
          <div style={{ width: 48 }} />
          <div style={s.logoCenterWrap}>
            <span style={s.logoText}>크루픽</span>
            <span style={s.logoSub}>title</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 2 }}>
            <NotificationBell />
          </div>
        </div>
      </div>

      {/* ─── 프로필 카드 ─── */}
      <div style={s.profileCard}>
        <div style={s.profileTop}>
          <div style={s.profileAvatar}>{userInitial}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={s.profileNick}>{nickname || "(닉네임 없음)"}</div>
            <div style={s.profileEmail}>{user?.email}</div>
          </div>
        </div>
      </div>

      <div style={s.body}>
        {/* ─── 섹션: 내 프로필 ─── */}
        <div style={s.section}>
          <div style={s.sectionTitle}>내 프로필</div>
          <button
            style={{ width: "100%", padding: "14px", background: "#fff", color: "#1A1A2E", border: "1.5px solid #1A1A2E", borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
            onClick={onViewProfile}>
            내 프로필 보기
          </button>
        </div>

        {/* ─── 섹션1: 내 팀 ─── */}
        <div style={s.section}>
          <div style={s.sectionTitle}>내 팀</div>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: 20 }}>
              <div style={s.spinner} />
            </div>
          ) : teams.length === 0 ? (
            <div style={{ padding: "16px", textAlign: "center", fontSize: 14, color: "#9ca3af", background: "#fff", borderRadius: 12, border: "1px solid #f3f4f6" }}>
              아직 소속된 팀이 없어요
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {teams.map((team) => {
                const sp = SPORTS[team.sports_type] || { icon: "🏅", label: team.sports_type };
                return (
                  <div
                    key={team.id}
                    style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", background: "#fff", borderRadius: 10, border: team.id === activeTeam?.id ? "2px solid #E8412A" : "1px solid #edf0f3", cursor: "pointer", boxShadow: "0 1px 4px rgba(0,0,0,0.04)" }}
                    onClick={() => { setActiveTeam(team); setSelectedTeam(team); }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <span style={{ fontSize: 22 }}>{sp.icon || "🏅"}</span>
                      <div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>{team.team_name}</div>
                        <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 2 }}>{sp.label}{team.level ? ` · ${team.level}` : ""}</div>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {team.myRole === "leader"
                        ? <span style={{ padding: "2px 8px", background: "#fef2f2", color: "#E24B4A", borderRadius: 999, fontSize: 11, fontWeight: 700 }}>팀장</span>
                        : <span style={{ padding: "2px 8px", background: "#f3f4f6", color: "#6b7280", borderRadius: 999, fontSize: 11, fontWeight: 700 }}>팀원</span>
                      }
                      <span style={{ fontSize: 14, color: "#d1d5db" }}>{">"}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <button
            style={{ width: "100%", marginTop: 10, padding: "13px", background: "#fff", border: "1.5px dashed #e5e7eb", borderRadius: 10, fontSize: 14, fontWeight: 600, color: "#9ca3af", cursor: "pointer", fontFamily: "inherit" }}
            onClick={onRegisterNew}
          >
            + 새 팀 등록하기
          </button>
        </div>

        {/* ─── 섹션2: 팀 찾기 ─── */}
        <div style={s.section}>
          <div style={s.sectionTitle}>팀 찾기</div>
          <div style={s.menuGroup}>
            <MenuRow label="팀 찾기" onClick={onTeamSearch} />
            <div style={s.menuDivider} />
            <MenuRow label="우리 팀 교류전 목록" onClick={onMyMatchEvents} />
          </div>
        </div>

        {/* ─── 섹션3: 회원 모집 ─── */}
        <div style={s.section}>
          <div style={s.sectionTitle}>회원 모집</div>
          <div style={s.menuGroup}>
            <MenuRow label="회원 모집 글" onClick={onClubRecruitment} />
          </div>
        </div>

        {/* ─── 섹션4: 게시판 ─── */}
        <div style={s.section}>
          <div style={s.sectionTitle}>게시판</div>
          <div style={s.menuGroup}>
            <MenuRow label="게시판" onClick={onBoard} />
            <div style={s.menuDivider} />
            <MenuRow label="📖 사용 가이드" onClick={onGuide} />
          </div>
        </div>

        {/* ─── 섹션5: 기타 ─── */}
        <div style={s.section}>
          <div style={s.sectionTitle}>기타</div>
          <div style={s.menuGroup}>
            <MenuRow label="🔔 알림 설정" onClick={() => setShowNotifSettings(true)} />
            <div style={s.menuDivider} />
            <MenuRow label="공지사항" onClick={onNotice} />
            <div style={s.menuDivider} />
            <MenuRow label="이용약관" onClick={() => setTermsModal("terms")} />
            <div style={s.menuDivider} />
            <MenuRow label="개인정보처리방침" onClick={onPrivacy} />
            <div style={s.menuDivider} />
            <MenuRow label="고객지원" onClick={onSupport} />
            <div style={s.menuDivider} />
            <MenuRow label="문의하기" onClick={onInquiry} />
            <div style={s.menuDivider} />
            <MenuRow label="버전 정보" onClick={handleVersionCheck} rightText={`v${APP_VERSION}`} />
            <div style={s.menuDivider} />
            <MenuRow label="로그아웃" onClick={handleSignOut} color="#E24B4A" />
            <div style={s.menuDivider} />
            <MenuRow label="계정 삭제" onClick={() => setShowDeleteConfirm(true)} color="#9ca3af" />
          </div>
        </div>
      </div>

      {/* 로그아웃 확인 모달 */}
      {showLogoutConfirm && (
        <div style={sm.backdrop} onClick={() => setShowLogoutConfirm(false)}>
          <div style={{ ...sm.box, padding: "28px 24px 20px", textAlign: "center", maxWidth: "calc(100% - 48px)", width: 320 }} onClick={(e) => e.stopPropagation()}>
            <p style={{ fontSize: 16, fontWeight: 600, color: "#111827", margin: "0 0 20px" }}>로그아웃 하시겠어요?</p>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setShowLogoutConfirm(false)} style={{ flex: 1, padding: "12px 0", borderRadius: 10, border: "1px solid #e5e7eb", background: "#f9fafb", fontSize: 14, fontWeight: 600, color: "#6b7280", cursor: "pointer" }}>취소</button>
              <button onClick={async () => { setShowLogoutConfirm(false); await signOut(); }} style={{ flex: 1, padding: "12px 0", borderRadius: 10, border: "none", background: "#E24B4A", fontSize: 14, fontWeight: 600, color: "#fff", cursor: "pointer" }}>로그아웃</button>
            </div>
          </div>
        </div>
      )}

      {/* 알림 설정 모달 */}
      {showNotifSettings && (
        <NotificationSettingsModal onClose={() => setShowNotifSettings(false)} />
      )}

      {/* 계정 삭제 확인 모달 */}
      {showDeleteConfirm && (
        <div style={sm.backdrop} onClick={() => !deleteLoading && setShowDeleteConfirm(false)}>
          <div style={{ ...sm.box, padding: "28px 24px 20px", textAlign: "center", maxWidth: "calc(100% - 48px)", width: 320 }} onClick={(e) => e.stopPropagation()}>
            <p style={{ fontSize: 16, fontWeight: 600, color: "#111827", margin: "0 0 8px" }}>계정을 삭제하시겠어요?</p>
            <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 20px", lineHeight: 1.6 }}>계정 삭제 시 모든 데이터가 영구적으로 삭제되며 복구할 수 없습니다.</p>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setShowDeleteConfirm(false)} disabled={deleteLoading} style={{ flex: 1, padding: "12px 0", borderRadius: 10, border: "1px solid #e5e7eb", background: "#f9fafb", fontSize: 14, fontWeight: 600, color: "#6b7280", cursor: "pointer", opacity: deleteLoading ? 0.5 : 1 }}>취소</button>
              <button onClick={handleDeleteAccount} disabled={deleteLoading} style={{ flex: 1, padding: "12px 0", borderRadius: 10, border: "none", background: "#E24B4A", fontSize: 14, fontWeight: 600, color: "#fff", cursor: "pointer", opacity: deleteLoading ? 0.5 : 1 }}>{deleteLoading ? "삭제 중..." : "삭제하기"}</button>
            </div>
          </div>
        </div>
      )}

      {/* 토스트 메시지 */}
      {toast && (
        <div style={{ position: "fixed", bottom: 80, left: "50%", transform: "translateX(-50%)", background: "rgba(0,0,0,0.8)", color: "#fff", padding: "10px 24px", borderRadius: 20, fontSize: 13, fontWeight: 500, zIndex: 999, whiteSpace: "nowrap" }}>{toast}</div>
      )}

      {/* 약관 모달 */}
      {termsModal && (
        <div style={sm.backdrop} onClick={() => setTermsModal(null)}>
          <div style={sm.box} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#111827", marginBottom: 14 }}>
              {termsModal === "terms" ? "이용약관" : "개인정보처리방침"}
            </div>
            <div style={{ fontSize: 13, color: "#6b7280", lineHeight: 1.8, marginBottom: 20, maxHeight: "50vh", overflowY: "auto" }}>
              {termsModal === "terms"
                ? "크루픽 이용약관\n\n1. 서비스 소개\n크루픽은 아마추어 스포츠 동호회 간 교류전 매칭 서비스입니다.\n\n2. 이용 조건\n· 만 14세 이상 이용 가능\n· Google 계정으로 로그인 필요\n· 허위 정보 등록 금지\n\n3. 서비스 이용 규칙\n· 타 사용자에 대한 비방, 욕설 금지\n· 허위 팀 정보 등록 금지\n· 매칭 후 무단 불참 금지\n· 상업적 목적의 이용 금지\n\n4. 서비스 중단 및 변경\n· 운영상 필요에 따라 서비스 내용 변경 가능\n· 중요 변경사항은 사전 공지\n\n5. 면책조항\n· 크루픽은 팀 간 분쟁에 대해 책임지지 않음\n· 이용자가 제공한 정보의 정확성에 대해 책임지지 않음\n\n6. 약관 변경\n· 약관 변경 시 앱 내 공지사항을 통해 안내\n\n7. 문의처\n이메일: gangsada@naver.com\n\n시행일: 2026년 3월 29일"
                : "크루픽 개인정보처리방침\n\n제1조(수집하는 개인정보)\n이메일 주소, 소셜 로그인 정보(구글 계정), 팀 정보(팀명, 종목, 지역 등)를 수집합니다.\n\n제2조(수집 목적)\n회원 식별 및 서비스 제공, 매칭 서비스 운영, 공지사항 및 서비스 안내에 활용됩니다.\n\n제3조(보유 기간)\n회원 탈퇴 시까지 보관하며 관련 법령에 따라 필요한 경우 해당 기간 동안 보관합니다.\n\n제4조(제3자 제공)\n이용자의 동의 없이 개인정보를 제3자에게 제공하지 않습니다.\n\n제5조(개인정보 보호 책임자)\n이메일: gangsada@naver.com\n\n시행일: 2026년 3월 29일"}
            </div>
            <button style={{ ...sm.closeBtn, flex: "none", width: "100%" }} onClick={() => setTermsModal(null)}>닫기</button>
          </div>
        </div>
      )}

      {selectedTeam && (
        <MyTeamDetailModal
          team={selectedTeam}
          userId={user?.id}
          onClose={() => setSelectedTeam(null)}
          onEdit={() => { setSelectedTeam(null); onEditTeam(selectedTeam); }}
          onDelete={() => handleDeleteTeamFromModal(selectedTeam)}
        />
      )}

      {/* 팀 선택 모달 */}
      {showTeamPicker && (
        <div style={sm.backdrop} onClick={() => setShowTeamPicker(false)}>
          <div style={sm.box} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: 17, fontWeight: 700, color: "#111827", marginBottom: 16 }}>팀 선택</div>
            {teams.map((team) => {
              const sport = SPORTS[team.sports_type] || { label: team.sports_type };
              const isActive = activeTeam?.id === team.id;
              return (
                <div
                  key={team.id}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "14px 12px", borderRadius: 12, marginBottom: 8, cursor: "pointer",
                    background: isActive ? "#fff5f5" : "#f7f8fa",
                    border: isActive ? "1.5px solid #E24B4A" : "1px solid #edf0f3",
                  }}
                  onClick={() => {
                    setActiveTeam(team);
                    setShowTeamPicker(false);
                  }}
                >
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: "#111827", marginBottom: 2 }}>{team.team_name}</div>
                    <div style={{ fontSize: 12, color: "#9ca3af" }}>{sport.label} · {team.level || "-"}</div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {team.myRole === "leader"
                      ? <span style={{ padding: "2px 8px", background: "#fef2f2", color: "#E24B4A", borderRadius: 999, fontSize: 11, fontWeight: 700, flexShrink: 0 }}>팀장</span>
                      : <span style={{ padding: "2px 8px", background: "#f3f4f6", color: "#6b7280", borderRadius: 999, fontSize: 11, fontWeight: 700, flexShrink: 0 }}>팀원</span>
                    }
                    <button
                      style={{ background: "none", border: "none", cursor: "pointer", padding: 4, color: "#9ca3af" }}
                      onClick={(e) => { e.stopPropagation(); setShowTeamPicker(false); setSelectedTeam(team); }}
                    >
                      상세
                    </button>
                    <button
                      style={{ background: "none", border: "none", cursor: "pointer", padding: 4, color: "#ef4444", display: "flex", alignItems: "center" }}
                      onClick={(e) => { e.stopPropagation(); setDeleteTarget(team); }}
                      title="팀 삭제"
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                    </button>
                  </div>
                </div>
              );
            })}
            <button style={{ ...sm.closeBtn, flex: "none", width: "100%", marginTop: 8 }} onClick={() => setShowTeamPicker(false)}>닫기</button>
          </div>
        </div>
      )}

      {/* 팀 삭제 확인 모달 */}
      {deleteTarget && (
        <div style={{ ...sm.backdrop, zIndex: 500 }} onClick={() => !teamDeleting && setDeleteTarget(null)}>
          <div style={{ ...sm.box, padding: "28px 24px 20px", textAlign: "center", maxWidth: "calc(100% - 48px)", width: 320 }} onClick={(e) => e.stopPropagation()}>
            <p style={{ fontSize: 16, fontWeight: 700, color: "#111827", margin: "0 0 8px" }}>팀 삭제</p>
            <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 20px", lineHeight: 1.6, whiteSpace: "pre-line" }}>
              {`'${deleteTarget.team_name}' 팀을 삭제하시겠습니까?\n삭제된 팀은 복구할 수 없습니다.`}
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setDeleteTarget(null)} disabled={teamDeleting} style={{ flex: 1, padding: "12px 0", borderRadius: 10, border: "1px solid #e5e7eb", background: "#f9fafb", fontSize: 14, fontWeight: 600, color: "#6b7280", cursor: "pointer", opacity: teamDeleting ? 0.5 : 1 }}>취소</button>
              <button onClick={confirmDeleteTeam} disabled={teamDeleting} style={{ flex: 1, padding: "12px 0", borderRadius: 10, border: "none", background: "#E24B4A", fontSize: 14, fontWeight: 600, color: "#fff", cursor: "pointer", opacity: teamDeleting ? 0.5 : 1 }}>{teamDeleting ? "삭제 중..." : "삭제"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MyTeamDetailModal({ team, userId, onClose, onEdit, onDelete }) {
  const [viewerSrc,   setViewerSrc]   = useState(null);
  const [memberCount, setMemberCount] = useState(0);
  const isLeader = team.user_id === userId;

  useEffect(() => {
    if (!isLeader) return;
    const fetchCount = async () => {
      const { count } = await supabase.from("team_members")
        .select("id", { count: "exact", head: true })
        .eq("team_id", team.id).eq("status", "active");
      setMemberCount(count || 0);
    };
    fetchCount();
  }, [team.id, isLeader]); // eslint-disable-line

  const handleCopyInviteText = async () => {
    const teamName = team?.team_name || "우리 팀";
    const text = `🎾 크루픽해서 교류전해요!\n\n${teamName}에서 함께 운동할 팀원을 찾고 있어요.\n\n1. 앱스토어/플레이스토어에서 '크루픽' 검색 후 설치\n2. 회원가입 → 하단 '내 정보' 탭\n3. '팀 찾기' → '${teamName}' 검색\n4. '팀원 가입 신청' 누르기\n\n신청해주시면 팀장이 수락할게요! 💪`;
    try {
      await navigator.clipboard.writeText(text);
      alert("초대 문구가 복사되었어요! 카톡 등에 붙여넣기 하세요 📋");
    } catch (e) {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      alert("초대 문구가 복사되었어요! 📋");
    }
  };

  const sport        = SPORTS[team.sports_type] || { icon: "🏅", label: team.sports_type };
  const totalMembers = (team.male_count || 0) + (team.female_count || 0);
  const availDays    = parseArray(team.available_days);
  const availTimes   = parseArray(team.available_times);

  return (
    <div style={sm.backdrop} onClick={onClose}>
      <div style={sm.box} onClick={(e) => e.stopPropagation()}>
        {/* 히어로 */}
        <div style={{ textAlign: "center", marginBottom: 14 }}>
          {team.logo_url ? (
            <img src={team.logo_url} alt="팀 로고" loading="lazy"
              style={{ width: 64, height: 64, borderRadius: 14, objectFit: "cover", border: "2px solid #e5e7eb", marginBottom: 8, cursor: "pointer" }}
              onClick={() => setViewerSrc(team.logo_url)}
            />
          ) : (
            <div style={{ fontSize: 38, marginBottom: 6 }}>{sport.icon || <img src={pickleballIcon} alt="피클볼" style={{ width: 38, height: 38, objectFit: "contain" }} />}</div>
          )}
          <div style={{ fontSize: 17, fontWeight: 700, color: "#111827", marginBottom: 2 }}>{team.team_name}</div>
          <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 6 }}>{sport.label}</div>
          <div style={{ display: "flex", gap: 5, justifyContent: "center", flexWrap: "wrap" }}>
            {team.level && (
              <span style={{ padding: "3px 10px", background: "#fff5f5", border: "1px solid #E24B4A", borderRadius: 999, fontSize: 11, fontWeight: 700, color: "#E24B4A" }}>
                {team.level}
              </span>
            )}
            {team.skill_level && (
              <span style={{ padding: "3px 10px", background: "#f0f9ff", border: "1px solid #bae6fd", borderRadius: 999, fontSize: 11, fontWeight: 700, color: "#0369a1" }}>
                {team.skill_level}
              </span>
            )}
          </div>
          {(team.match_level != null || team.manner_avg != null) && (
            <div style={{ display: "flex", gap: 8, justifyContent: "center", marginTop: 8 }}>
              {team.match_level != null && <LevelBadge level={team.match_level} />}
              {team.manner_avg != null && <MannerBadge score={team.manner_avg} />}
            </div>
          )}
          {team.region && <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 6 }}>서울 {team.region}</div>}
        </div>

        {/* 단체사진 */}
        {team.team_photo && (
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 4 }}>팀 단체사진</div>
            <img src={team.team_photo} alt="단체사진" loading="lazy"
              style={{ width: "100%", height: 120, objectFit: "cover", borderRadius: 10, border: "1px solid #e5e7eb", cursor: "pointer", display: "block" }}
              onClick={() => setViewerSrc(team.team_photo)}
            />
          </div>
        )}

        {/* 팀 정보 */}
        <div style={{ background: "#f5f6fa", borderRadius: 10, padding: "2px 12px", marginBottom: 10 }}>
          <MyTeamInfoRow label="구력 레벨"  value={formatSportLevel(team.sports_type, team.level)} />
          {team.skill_level && <MyTeamInfoRow label="실력" value={team.skill_level} />}
          <MyTeamInfoRow label="전적 레벨"  value={formatRecordLevel(team)} />
          <MyTeamInfoRow label="전적"       value={formatRecord(team)} />
          <MyTeamInfoRow label="매너 점수"  value={formatManner(team)} />
          <MyTeamInfoRow label="총 인원"    value={`${totalMembers}명 (남 ${team.male_count || 0} / 여 ${team.female_count || 0})`} />
          <MyTeamInfoRow label="연령대"     value={parseAgeGroup(team.age_group) || "-"} />
          <MyTeamInfoRow label="평균 구력"  value={team.member_avg_career || "-"} />
          {team.main_place    && <MyTeamInfoRow label="주 활동 장소"    value={team.main_place} />}
          {availDays.length > 0  && <MyTeamInfoRow label="매칭 가능 요일" value={availDays.join(", ")} />}
          {availTimes.length > 0 && <MyTeamInfoRow label="매칭 가능 시간" value={availTimes.join(", ")} />}
          {team.parking != null && (
            <MyTeamInfoRow label="주차" value={team.parking ? `가능${team.parking_count ? ` (${team.parking_count}대)` : ""}` : "불가능"} />
          )}
        </div>

        {/* 팀 소개 */}
        {team.intro && (
          <div style={{ background: "#f5f6fa", borderRadius: 10, padding: "10px 12px", marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 4 }}>팀 소개</div>
            <div style={{ fontSize: 12, color: "#6b7280", lineHeight: 1.6 }}>{team.intro}</div>
          </div>
        )}

        {/* 운영진 */}
        <div style={{ marginBottom: 10 }}>
          <TeamMemberPanel
            teamId={team.id}
            currentUserId={userId}
            isLeader={team.user_id === userId}
            showTransfer={true}
          />
        </div>

        {/* 팀원 초대하기 (팀장만) */}
        {isLeader && (
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 6 }}>팀원 초대하기</div>
            <div style={{ background: "#f5f6fa", borderRadius: 10, border: "1px solid #f3f4f6", padding: "14px" }}>
              <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 10 }}>아래 문구를 복사해서 카톡 등으로 공유하세요</div>
              <div style={{ background: "#f3f4f6", borderRadius: 8, padding: 12, fontSize: 12, color: "#374151", lineHeight: 1.7, whiteSpace: "pre-line", marginBottom: 10 }}>
                {`🎾 크루픽해서 교류전해요!\n\n${team?.team_name || "우리 팀"}에서 함께 운동할 팀원을 찾고 있어요.\n\n1. 앱스토어/플레이스토어에서 '크루픽' 검색 후 설치\n2. 회원가입 → 하단 '내 정보' 탭\n3. '팀 찾기' → '${team?.team_name || "우리 팀"}' 검색\n4. '팀원 가입 신청' 누르기\n\n신청해주시면 팀장이 수락할게요! 💪`}
              </div>
              <button
                style={{ width: "100%", padding: "11px", background: "#E8412A", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
                onClick={handleCopyInviteText}
              >
                📋 초대 문구 복사하기
              </button>
            </div>
          </div>
        )}

        {/* 버튼 영역 */}
        <div style={{ display: "flex", gap: 8 }}>
          <button style={sm.closeBtn} onClick={onClose}>닫기</button>
          {isLeader && (
            <>
              <button style={sm.deleteTeamBtn} onClick={onDelete}>팀 삭제</button>
              <button style={sm.editBtn} onClick={onEdit}>수정하기</button>
            </>
          )}
        </div>
      </div>
      {viewerSrc && <ImageViewer src={viewerSrc} onClose={() => setViewerSrc(null)} />}
    </div>
  );
}

function MyTeamInfoRow({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "11px 0", borderBottom: "1px solid #ebebf0" }}>
      <span style={{ fontSize: 12, color: "#9ca3af", flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, color: "#374151", fontWeight: 600, textAlign: "right", marginLeft: 8 }}>{value}</span>
    </div>
  );
}

const sm = {
  backdrop: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 300, backdropFilter: "blur(4px)" },
  box:      { width: "90%", maxWidth: 360, background: "#fff", borderRadius: 18, padding: "20px 18px 24px", boxShadow: "0 8px 32px rgba(0,0,0,0.20)", maxHeight: "85vh", overflowY: "auto" },
  closeBtn:      { flex: 1, padding: "11px", background: "#f7f8fa", color: "#374151", border: "1px solid #edf0f3", borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: "pointer" },
  editBtn:       { flex: 2, padding: "11px", background: "#E24B4A", color: "#fff", border: "none", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer" },
  deleteTeamBtn: { flex: 1, padding: "11px", background: "#fff", color: "#E24B4A", border: "1.5px solid #E24B4A", borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: "pointer" },
};

const s = {
  container: { minHeight: "100dvh", background: "#FFFFFF", display: "flex", flexDirection: "column", maxWidth: 430, margin: "0 auto", fontFamily: "'Noto Sans KR', sans-serif", overflowY: "auto", paddingBottom: 80 },
  header: { padding: "calc(env(safe-area-inset-top) + 18px) 20px 16px", borderBottom: "1px solid #edf0f3", background: "#fff", position: "sticky", top: 0, zIndex: 10, boxShadow: "0 1px 6px rgba(0,0,0,0.04)" },
  logoRow: { position: "relative", display: "flex", alignItems: "center", justifyContent: "space-between" },
  logoCenterWrap: { position: "absolute", left: "50%", transform: "translateX(-50%)", display: "flex", alignItems: "baseline", gap: 4 },
  logoText: { fontSize: 20, fontWeight: 800, color: "#111827", letterSpacing: "-0.5px" },
  logoSub:  { fontSize: 12, color: "#9ca3af", fontWeight: 400 },
  spinner: { width: 28, height: 28, border: "3px solid #edf0f3", borderTop: "3px solid #E24B4A", borderRadius: "50%", animation: "spin 0.8s linear infinite" },

  /* 프로필 카드 */
  profileCard: { margin: "20px 20px 0", background: "#fff", borderRadius: 16, border: "1px solid #edf0f3", padding: "20px", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" },
  profileTop: { display: "flex", alignItems: "center", gap: 14, marginBottom: 14 },
  profileAvatar: { width: 52, height: 52, background: "#E24B4A", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 700, color: "#fff", flexShrink: 0 },
  profileNick: { fontSize: 17, fontWeight: 700, color: "#111827", marginBottom: 2 },
  profileEmail: { fontSize: 13, color: "#9ca3af" },

  /* 본문 */
  body: { flex: 1, padding: "8px 20px 100px" },

  /* 섹션 */
  section: { marginTop: 24 },
  sectionTitle: { fontSize: 13, fontWeight: 500, color: "#9ca3af", marginBottom: 8 },

  /* 메뉴 그룹 */
  menuGroup: { background: "#fff", borderRadius: 12, border: "1px solid #f3f4f6", overflow: "hidden" },
  menuRow: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "15px 16px", cursor: "pointer" },
  menuLabel: { fontSize: 15, fontWeight: 500, color: "#1f2937" },
  menuArrow: { fontSize: 14, color: "#d1d5db", fontWeight: 400 },
  menuDivider: { height: 1, background: "#f3f4f6", margin: "0 16px" },

  /* 내 팀 정보 행 */
  teamInfoRow: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px" },
  teamInfoName: { fontSize: 15, fontWeight: 700, color: "#111827" },
  teamBadge: { padding: "3px 10px", background: "#f3f4f6", borderRadius: 999, fontSize: 11, fontWeight: 600, color: "#6b7280" },
  teamBadgeLevel: { padding: "3px 10px", background: "#fff5f5", border: "1px solid #E24B4A", borderRadius: 999, fontSize: 11, fontWeight: 700, color: "#E24B4A" },
};
